// ═══════════════════════════════════════════════════════════════
// DIRECTOR SERVICE — CRUD for directors table
// Path: src/services/directorService.js
//
// Directors are relationship managers who own donor portfolios.
// Managed in Settings → Directors tab.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

export const directorService = {
  // ── Get all directors ─────────────────────────────────────
  async getAll() {
    const { data, error } = await supabase
      .from("directors")
      .select("*")
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      debugLog.error("directorService.getAll", error);
      throw error;
    }
    return data || [];
  },

  // ── Get active directors only ─────────────────────────────
  async getActive() {
    const { data, error } = await supabase
      .from("directors")
      .select("*")
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("name", { ascending: true });

    if (error) {
      debugLog.error("directorService.getActive", error);
      throw error;
    }
    return data || [];
  },

  // ── Create a director ─────────────────────────────────────
  async create(directorData) {
    const { data, error } = await supabase
      .from("directors")
      .insert({
        name: directorData.name,
        email: directorData.email || null,
        phone: directorData.phone || null,
        title: directorData.title || null,
        is_active: true,
        sort_order: directorData.sortOrder || 0,
      })
      .select()
      .single();

    if (error) {
      debugLog.error("directorService.create", error);
      throw error;
    }
    debugLog.action("directorService.create", { name: directorData.name });
    return data;
  },

  // ── Update a director ─────────────────────────────────────
  async update(id, updates) {
    const row = {};
    if (updates.name !== undefined) row.name = updates.name;
    if (updates.email !== undefined) row.email = updates.email || null;
    if (updates.phone !== undefined) row.phone = updates.phone || null;
    if (updates.title !== undefined) row.title = updates.title || null;
    if (updates.isActive !== undefined) row.is_active = updates.isActive;
    if (updates.sortOrder !== undefined) row.sort_order = updates.sortOrder;
    row.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from("directors")
      .update(row)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      debugLog.error("directorService.update", error);
      throw error;
    }
    debugLog.action("directorService.update", { id, ...row });
    return data;
  },

  // ── Remove a director ─────────────────────────────────────
  async remove(id) {
    const { error } = await supabase
      .from("directors")
      .delete()
      .eq("id", id);

    if (error) {
      debugLog.error("directorService.remove", error);
      throw error;
    }
    debugLog.action("directorService.remove", { id });
  },
};
